'use client'

import React, { useState } from 'react'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'

// ============================================================================
// Types
// ============================================================================

interface MissingParam {
  name: string
  description: string
  type: string
  required: boolean
  enum?: string[]
  default?: string | number | boolean
  placeholder?: string
}

interface ClarificationFormProps {
  params: Record<string, MissingParam>
  message?: string
  onSubmit: (values: Record<string, string | number | boolean>) => void
  onCancel?: () => void
}

// ============================================================================
// Clarification Form
// ============================================================================

export function ClarificationForm({ params, message, onSubmit, onCancel }: ClarificationFormProps) {
  const [values, setValues] = useState<Record<string, string | number | boolean>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleChange = (name: string, value: string | number | boolean) => {
    setValues(prev => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate required fields
    const newErrors: Record<string, string> = {}
    Object.entries(params).forEach(([name, param]) => {
      if (param.required && !values[name]) {
        newErrors[name] = `${param.description || name} is required`
      }
    })

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    onSubmit(values)
  }

  return (
    <div className="border-l-4 border-amber-500 bg-amber-50 rounded-lg p-4 my-3">
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <Icons.alertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="font-semibold text-amber-900">Additional Information Needed</h4>
          <p className="text-sm text-amber-700 mt-1">
            {message || 'Please provide the following information to continue:'}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        {Object.entries(params).map(([name, param]) => (
          <FormField
            key={name}
            name={name}
            param={param}
            value={values[name] || ''}
            error={errors[name]}
            onChange={handleChange}
          />
        ))}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2">
          <Button
            type="submit"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Icons.check className="h-4 w-4 mr-1.5" />
            Continue
          </Button>
          
          {onCancel && (
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              className="text-amber-900 hover:text-amber-900 hover:bg-amber-100"
            >
              Cancel
            </Button>
          )}
        </div>
      </form>
    </div>
  )
}

// ============================================================================
// Form Field Component
// ============================================================================

interface FormFieldProps {
  name: string
  param: MissingParam
  value: string | number | boolean
  error?: string
  onChange: (name: string, value: string | number | boolean) => void
}

function FormField({ name, param, value, error, onChange }: FormFieldProps) {
  const inputId = `param-${name}`

  return (
    <div>
      <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-1">
        {param.description || name}
        {param.required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {param.type === 'select' && param.enum ? (
        <select
          id={inputId}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(name, e.target.value)}
          className={`
            w-full rounded-md border px-3 py-2 text-sm
            ${error ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-brand-cornflower'}
            focus:outline-none focus:ring-1 focus:ring-brand-cornflower
          `}
        >
          <option value="">Select {param.description || name}</option>
          {param.enum.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : param.type === 'boolean' ? (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id={inputId}
            checked={value === true}
            onChange={(e) => onChange(name, e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-brand-cornflower focus:ring-brand-cornflower"
          />
          <label htmlFor={inputId} className="text-sm text-gray-600">
            {param.description || name}
          </label>
        </div>
      ) : param.type === 'number' ? (
        <input
          type="number"
          id={inputId}
          value={typeof value === 'number' ? value : ''}
          onChange={(e) => onChange(name, e.target.valueAsNumber)}
          placeholder={param.placeholder || `Enter ${param.description || name}`}
          className={`
            w-full rounded-md border px-3 py-2 text-sm
            ${error ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-brand-cornflower'}
            focus:outline-none focus:ring-1 focus:ring-brand-cornflower
          `}
        />
      ) : (
        <input
          type="text"
          id={inputId}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(name, e.target.value)}
          placeholder={param.placeholder || `Enter ${param.description || name}`}
          className={`
            w-full rounded-md border px-3 py-2 text-sm
            ${error ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-brand-cornflower'}
            focus:outline-none focus:ring-1 focus:ring-brand-cornflower
          `}
        />
      )}

      {error && (
        <p className="text-sm text-red-600 mt-1">{error}</p>
      )}
    </div>
  )
}
